import { all, allPass, compose, filter, find, prop, propEq } from "ramda"
import {
  isNilOrEmpty,
  isNotNil,
  isSystemAdmin,
  getNodeClasses,
  user,
  userProp,
  isNotNilOrEmpty,
  isFeatureEnabled,
} from "js/includes/common/utils"

const checkAccess = ({ entityType, entityId, permissionName: name }) => {
  if (isSystemAdmin()) return true
  const permissions = userProp("permissions")
  if (isNilOrEmpty(permissions)) return false

  const filteredPermissionsByName = filter(allPass([propEq("entityType", entityType), propEq("name", name)]))(
    permissions,
  )
  if (isNilOrEmpty(filteredPermissionsByName)) return null

  const permissionById = find(propEq("entityId", entityId))(filteredPermissionsByName)
  if (permissionById) return permissionById.allowed

  return find(propEq("entityId", 0))(filteredPermissionsByName)?.allowed ?? null
}

export const isGranted = permission => {
  return !!checkAccess(permission)
}

export const isGrantedForAtLeastOneEntity = ({ entityType, permissionName: name }) => {
  if (isSystemAdmin()) return true
  const permissions = userProp("permissions")
  if (isNilOrEmpty(permissions)) return false

  const matchedPermission = find(
    allPass([propEq("entityType", entityType), propEq("name", name), propEq("allowed", true)]),
  )(permissions)

  return isNotNil(matchedPermission)
}

export const isGrantedForAllEntities = ({ entityType, permissionName: name }) => {
  if (isSystemAdmin()) return true
  const permissions = userProp("permissions")
  if (isNilOrEmpty(permissions)) return false

  const entityPermissions = filter(allPass([propEq("entityType", entityType), propEq("name", name)]))(permissions)

  if (isNilOrEmpty(entityPermissions)) return false

  const nonAllowedPermissions = filter(propEq("allowed", false), entityPermissions)
  const defaultPermission = filter(propEq("entityId", 0), entityPermissions)

  return isNilOrEmpty(nonAllowedPermissions) && isNotNilOrEmpty(defaultPermission)
}

export const isNodeGranted = ({ permissionName, nodeClass, nodeRoleId }) => {
  if (isSystemAdmin()) return true
  const rolePermission = checkAccess({
    entityType: "NODE",
    permissionName: `Role.${permissionName}`,
    entityId: nodeRoleId,
  })
  if (rolePermission !== null) return rolePermission

  const classPermission = checkAccess({
    entityType: "NODE",
    permissionName: `Class.${permissionName}`,
    entityId: compose(prop("id"), find(propEq("name", nodeClass)))(getNodeClasses()),
  })
  if (classPermission !== null) return classPermission

  return isGranted({
    entityType: "NODE",
    permissionName,
    entityId: 0,
  })
}

export const isNodeGrantedForAtLeastOneEntity = ({ permissionName }) => {
  if (isSystemAdmin()) return true
  return (
    isGrantedForAtLeastOneEntity({ entityType: "NODE", permissionName: `Role.${permissionName}` }) ||
    isGrantedForAtLeastOneEntity({ entityType: "NODE", permissionName: `Class.${permissionName}` }) ||
    isGrantedForAtLeastOneEntity({ entityType: "NODE", permissionName })
  )
}

export const hasDeviceAdministrationAccess = () => {
  return isFeatureEnabled("user_permission")
    ? isGranted({ entityType: "SYSTEM", permissionName: "Device.Administration.Access" })
    : isSystemAdmin()
}

export const hasDeviceSecureFieldsAccess = () => {
  return isFeatureEnabled("user_permission")
    ? isGranted({ entityType: "NODE", permissionName: "SecureFields.Access" })
    : isSystemAdmin()
}

export const hasDocumentationConfigurationAccess = () => {
  return isFeatureEnabled("user_permission")
    ? isGranted({ entityType: "DOCUMENTATION", permissionName: "Configuration.Access" })
    : isSystemAdmin()
}

const checkDocumentationManagementPermission = permission => {
  return isGranted({ entityType: "DOCUMENTATION", permissionName: `Management.${permission}` })
}

export const hasDocumentationManagementAccess = permission => {
  return isFeatureEnabled("user_permission")
    ? checkDocumentationManagementPermission(permission)
    : isSystemAdmin() || permission === "View"
}

export const hasPermissionToUpdateDevices = all(({ nodeClass, nodeRoleId }) => {
  return user("canUpdateDevices", { nodeClass, nodeRoleId })
})

const checkRelatedItemPermission = permission => {
  return isGranted({ entityType: "DOCUMENTATION", permissionName: `RelatedItem.${permission}` })
}

export const hasRelatedItemsAccess = permission => {
  return isFeatureEnabled("user_permission") ? checkRelatedItemPermission(permission) : isSystemAdmin()
}

const checkDocumentationGlobalKnowledgeBaseManagementPermission = permission => {
  return isGranted({ entityType: "DOCUMENTATION", permissionName: `Global.KB.Management.${permission}` })
}

export const hasDocumentationGlobalKnowledgeBaseManagementAccess = permission => {
  return isFeatureEnabled("user_permission")
    ? checkDocumentationGlobalKnowledgeBaseManagementPermission(permission)
    : isSystemAdmin() || permission === "View"
}

export const hasDocumentationKnowledgeBaseManagementAccess = (permission, clientId) =>
  clientId
    ? hasDocumentationManagementAccess(permission)
    : hasDocumentationGlobalKnowledgeBaseManagementAccess(permission)
